// app/src/lib/revenue/sankeyLayout.js — geometry for the cash-flow Sankey.
//
// Pulled out of the JSX so the one invariant that matters can actually be
// tested: EVERYTHING DRAWN FITS INSIDE THE VIEWBOX. The diagram had the Net
// profit node — the last item in the cost column — hanging out below the frame,
// because the frame height was derived from the stacks while two things could
// still push past it:
//
//   1. a tiny slice is clamped to a minimum bar height, so a column with many
//      small categories sums to MORE than the space allotted to it;
//   2. the label declutter pushes colliding labels downward, so the last label
//      can sit below the last bar.
//
// Both are legitimate — a hairline slice must stay visible, and labels must not
// overlap — so the fix is to let the frame follow them rather than assume it
// won't need to. All units are viewBox units.

export const VBW = 440;      // viewBox width
export const TOP = 10;       // top padding
export const DH = 250;       // nominal column height
export const GAP = 8;        // gap between stacked slices
export const BARW = 9;       // bar thickness
export const MIN_SLICE = 4;  // a slice never renders thinner than this
export const LABELGAP = 13;  // one line of label needs this much room
export const BOTTOM = 8;     // bottom padding

export const LBAR_L = 150, LBAR_R = LBAR_L + BARW;
export const CBAR_L = 214, CBAR_R = CBAR_L + BARW;
export const RBAR_L = 281, RBAR_R = RBAR_L + BARW;

// A label is ONE line: name and amount share a baseline at ly + 3, in ~11px
// type whose descenders reach about 3 below it. It used to be two stacked
// lines, which needed 22 units of separation and so pushed colliding labels a
// long way from the slice they belong to.
const LABEL_INK_BELOW = 7;
const LABEL_INK_ABOVE = 9;

/**
 * Stack one column so it fills the height EXACTLY, with small uniform gaps.
 *
 * Both columns carry the same total — revenue on the left, the same money spent
 * or kept on the right — so both are "the whole", and each fills the same
 * span. That is the Sankey reading: a column is the total, a slice is its share
 * of that total. Ends line up, gaps stay small, proportions are read WITHIN a
 * column.
 *
 * The part that used to break alignment is the hairline clamp. A 0.5% slice
 * rounds to about a unit and would vanish, so it is floored at MIN_SLICE — but
 * nothing paid for those extra units, so the column grew past its span and the
 * two sides ended at different heights. The height is now taken back from the
 * slices big enough to give it, proportionally, so the column still sums to
 * exactly its span. A hairline is therefore drawn slightly generously; the
 * printed percentage next to it remains the truth.
 */
function stack(items, columnTotal) {
  const n = items.length;
  if (!n) return [];
  const usable = DH - GAP * (n - 1);
  const total = columnTotal > 0 ? columnTotal : 1;
  let hs = items.map((it) => (it.cents / total) * usable);

  // Floor the hairlines, then reclaim the borrowed height from the slices that
  // can afford it. Iterated, because reclaiming can push another slice under
  // the floor; it converges in a couple of passes for any real invoice.
  for (let pass = 0; pass < 4; pass++) {
    const under = hs.map((h) => h < MIN_SLICE);
    if (!under.some(Boolean)) break;
    const borrowed = hs.reduce((s, h, i) => s + (under[i] ? MIN_SLICE - h : 0), 0);
    const donorTotal = hs.reduce((s, h, i) => s + (under[i] ? 0 : h), 0);
    if (donorTotal <= 0) break;                       // every slice is a hairline
    hs = hs.map((h, i) => (under[i] ? MIN_SLICE : h - (h / donorTotal) * borrowed));
  }

  let y = TOP;
  return items.map((it, i) => {
    const s = { ...it, y, h: hs[i] };
    y += hs[i] + GAP;
    return s;
  });
}

/** Push apart labels that would collide, leaving the BARS at true proportion. */
function declutter(segs) {
  let prev = -Infinity;
  return segs.map((s) => {
    let ly = s.y + s.h / 2;
    if (ly < prev + LABELGAP) ly = prev + LABELGAP;
    prev = ly;
    return { ...s, ly };
  });
}

/**
 * Lay out the diagram.
 *
 * @param {{revenueCents:number, totalCostsCents:number, netCents:number,
 *          sources:Array<{id:string,cents:number}>, costs:Array<{id:string,cents:number}>}} data
 * @returns {{H:number, denom:number, sources:Array, targets:Array}}
 */
export function sankeyLayout(data) {
  const revenue = data.revenueCents || 0;
  const costsTotal = data.totalCostsCents || 0;
  const net = data.netCents || 0;
  let sources = (data.sources || []).slice();
  let targets = (data.costs || []).slice();
  // The two columns must sum to the same total or the ribbons don't fill the
  // height: a profitable month books the surplus as a Net profit node, a losing
  // one books the gap as a shortfall source.
  if (revenue >= costsTotal) { if (net > 0) targets.push({ id: 'net', cents: net }); }
  else { sources = sources.concat([{ id: 'shortfall', cents: costsTotal - revenue }]); }
  const denom = Math.max(revenue, costsTotal, 1);

  // Each column fills the span using its OWN total, so both start at TOP and
  // end at TOP + DH. They carry the same money, so that is not a distortion —
  // it is the two sides of the same total, drawn at the same size.
  const sumOf = (a) => a.reduce((x, it) => x + (it.cents || 0), 0);
  const S = declutter(stack(sources, sumOf(sources)));
  const T = declutter(stack(targets, sumOf(targets)));

  // Two different bottoms, and conflating them is what put a grey rectangle
  // below the diagram:
  //   · the COLUMN bottom — how far the bars actually reach. The middle spine
  //     represents the total, so it must span exactly this.
  //   · the FRAME bottom — how far the LABELS reach. Declutter pushes colliding
  //     labels downward, so the last one can sit well below the last bar, and
  //     the frame has to grow to keep it visible.
  // The spine was sized from the frame, so every unit of label overhang became
  // a unit of bar hanging past the end of the diagram.
  const columnBottom = Math.max(TOP, ...[...S, ...T].map((x) => x.y + x.h));
  const labelBottom = Math.max(TOP, ...[...S, ...T].map((x) => x.ly + LABEL_INK_BELOW));
  const H = Math.max(DH + TOP, columnBottom, labelBottom) + BOTTOM;
  return {
    H, denom, sources: S, targets: T,
    // The spine spans the bars, never the label overhang.
    spine: { y: TOP, h: Math.max(MIN_SLICE, columnBottom - TOP) },
  };
}

/**
 * Every y-extent the renderer will draw, for the fits-in-frame assertion.
 * Kept beside the layout so a new drawn element has to be declared here too.
 */
export function drawnExtents(layout) {
  const out = [];
  for (const s of [...layout.sources, ...layout.targets]) {
    out.push({ what: `bar:${s.id}`, top: s.y, bottom: s.y + s.h });
    out.push({ what: `label:${s.id}`, top: s.ly - LABEL_INK_ABOVE, bottom: s.ly + LABEL_INK_BELOW });
  }
  out.push({ what: 'spine', top: layout.spine.y, bottom: layout.spine.y + layout.spine.h });
  return out;
}
